/**
 * CustomButtonService
 *
 * Service for fetching, evaluating visibility rules, and executing custom buttons
 * configured in the Object Manager Buttons & Actions tab.
 *
 * @module services/customButtonService
 */

import {
  ButtonAction,
  ButtonLocation,
  VisibilityRule,
  ActionConfig,
  ModalType
} from './queries/buttonActionQueries';

// Import icon mapping
import * as LucideIcons from 'lucide-react';

// ============================================
// TYPES
// ============================================

export interface ExecutionResult {
  success: boolean;
  message: string;
  data?: any;
}

export interface ButtonWithIcon extends ButtonAction {
  IconComponent?: React.ComponentType<{ className?: string }>;
}

// ============================================
// VISIBILITY EVALUATION
// ============================================

/**
 * Evaluates a single visibility rule against record data
 */
function evaluateRule(rule: VisibilityRule, recordData: Record<string, any>): boolean {
  const fieldValue = recordData[rule.fieldId];

  switch (rule.operator) {
    case 'equals':
      return fieldValue === rule.value;

    case 'notEquals':
      return fieldValue !== rule.value;

    case 'contains':
      if (typeof fieldValue === 'string' && typeof rule.value === 'string') {
        return fieldValue.toLowerCase().includes(rule.value.toLowerCase());
      }
      return false;

    case 'isBlank':
      return fieldValue === null ||
             fieldValue === undefined ||
             fieldValue === '' ||
             (Array.isArray(fieldValue) && fieldValue.length === 0);

    case 'isNotBlank':
      return fieldValue !== null &&
             fieldValue !== undefined &&
             fieldValue !== '' &&
             !(Array.isArray(fieldValue) && fieldValue.length === 0);

    default:
      return true;
  }
}

/**
 * Evaluates all visibility rules for a button
 * Returns true if the button should be visible
 */
export function evaluateVisibility(
  button: ButtonAction,
  recordData: Record<string, any>
): boolean {
  // If no rules, button is always visible
  if (!button.visibilityRules || button.visibilityRules.length === 0) {
    return true;
  }

  const results = button.visibilityRules.map(rule => evaluateRule(rule, recordData));

  // Apply logic (AND/OR)
  if (button.visibilityLogic === 'OR') {
    return results.some(r => r);
  }

  // Default to AND logic
  return results.every(r => r);
}

// ============================================
// BUTTON FETCHING
// ============================================

/**
 * Gets buttons for a specific object and location, filtered by visibility
 */
export function getButtonsForLocation(
  allButtons: ButtonAction[],
  objectName: string,
  location: ButtonLocation,
  recordData?: Record<string, any>
): ButtonWithIcon[] {
  // Filter by object, location, and active status
  const filtered = allButtons.filter(btn =>
    btn.objectName === objectName &&
    btn.location === location &&
    btn.isActive
  );

  // If record data provided, filter by visibility rules
  const visible = recordData
    ? filtered.filter(btn => evaluateVisibility(btn, recordData))
    : filtered;

  // Add icon components
  return visible.map(btn => ({
    ...btn,
    IconComponent: btn.icon ? getIconComponent(btn.icon) : undefined
  }));
}

/**
 * Gets all buttons for a detail page (header, section, footer)
 */
export function getDetailPageButtons(
  allButtons: ButtonAction[],
  objectName: string,
  recordData?: Record<string, any>
): {
  header: ButtonWithIcon[];
  section: ButtonWithIcon[];
  footer: ButtonWithIcon[];
} {
  return {
    header: getButtonsForLocation(allButtons, objectName, 'detail-header', recordData),
    section: getButtonsForLocation(allButtons, objectName, 'detail-section', recordData),
    footer: getButtonsForLocation(allButtons, objectName, 'detail-footer', recordData)
  };
}

// ============================================
// ICON MAPPING
// ============================================

/**
 * Gets the Lucide icon component by name
 */
export function getIconComponent(iconName: string): React.ComponentType<{ className?: string }> | undefined {
  // Handle common icon mappings
  const iconMap: Record<string, keyof typeof LucideIcons> = {
    'Mail': 'Mail',
    'Star': 'Star',
    'BarChart': 'BarChart',
    'Archive': 'Archive',
    'Edit': 'Edit',
    'Trash': 'Trash2',
    'Send': 'Send',
    'Plus': 'Plus',
    'Check': 'Check',
    'X': 'X',
    'Download': 'Download',
    'Upload': 'Upload',
    'Copy': 'Copy',
    'Link': 'Link',
    'ExternalLink': 'ExternalLink',
    'Share': 'Share2',
    'Eye': 'Eye',
    'EyeOff': 'EyeOff',
    'Lock': 'Lock',
    'Unlock': 'Unlock',
    'Settings': 'Settings',
    'User': 'User',
    'Users': 'Users',
    'Calendar': 'Calendar',
    'Clock': 'Clock',
    'Phone': 'Phone',
    'FileText': 'FileText',
    'Folder': 'Folder',
    'Tag': 'Tag',
    'Flag': 'Flag',
    'AlertCircle': 'AlertCircle',
    'CheckCircle': 'CheckCircle',
    'XCircle': 'XCircle',
    'Info': 'Info',
    'HelpCircle': 'HelpCircle',
    'Refresh': 'RefreshCw',
    'Play': 'Play',
    'Pause': 'Pause',
    'Stop': 'Square',
    'Zap': 'Zap',
    'DollarSign': 'DollarSign',
    'CreditCard': 'CreditCard',
    'TrendingUp': 'TrendingUp',
    'TrendingDown': 'TrendingDown'
  };

  const mappedName = iconMap[iconName] || iconName;
  const IconComponent = (LucideIcons as any)[mappedName];

  return IconComponent || LucideIcons.Circle;
}

// ============================================
// BUTTON EXECUTION
// ============================================

/**
 * Executes a button action
 */
export async function executeButtonAction(
  button: ButtonAction,
  recordData: Record<string, any>,
  recordId: string,
  callbacks?: {
    onUpdateFields?: (updates: Record<string, any>) => Promise<void>;
    onNavigate?: (url: string, newTab: boolean) => void;
    onShowToast?: (type: 'success' | 'error', message: string) => void;
    onOpenModal?: (modalType: ModalType, recordData: Record<string, any>) => void;
  }
): Promise<ExecutionResult> {
  const { action } = button;

  try {
    switch (action.type) {
      case 'call-api':
        return await executeApiCall(action, recordData, recordId);

      case 'run-workflow':
        return await executeWorkflow(action, recordData, recordId);

      case 'update-fields':
        return await executeFieldUpdates(action, recordData, recordId, callbacks?.onUpdateFields);

      case 'navigate':
        return executeNavigation(action, recordData, recordId, callbacks?.onNavigate);

      case 'javascript':
        return executeJavaScript(action, recordData, recordId);

      case 'open-modal':
        return executeOpenModal(action, recordData, callbacks?.onOpenModal);

      default:
        return {
          success: false,
          message: `Unknown action type: ${action.type}`
        };
    }
  } catch (error) {
    console.error('Button execution error:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'An unexpected error occurred'
    };
  }
}

/**
 * Execute API call action
 */
async function executeApiCall(
  action: ActionConfig,
  recordData: Record<string, any>,
  recordId: string
): Promise<ExecutionResult> {
  if (!action.apiUrl) {
    return { success: false, message: 'API URL not configured' };
  }

  // Replace placeholders in URL
  let url = action.apiUrl.replace('{recordId}', recordId);

  // Replace field placeholders
  Object.keys(recordData).forEach(key => {
    url = url.replace(`{${key}}`, String(recordData[key] || ''));
  });

  try {
    // In mock mode, simulate API call
    console.log('Mock API Call:', {
      method: action.apiMethod || 'GET',
      url,
      headers: action.apiHeaders,
      body: action.apiBody
    });

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // For now, return success (real implementation would use fetch/axios)
    return {
      success: true,
      message: 'API call completed successfully',
      data: { url, method: action.apiMethod }
    };
  } catch (error) {
    return {
      success: false,
      message: `API call failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Execute workflow action
 */
async function executeWorkflow(
  action: ActionConfig,
  recordData: Record<string, any>,
  recordId: string
): Promise<ExecutionResult> {
  if (!action.workflowId) {
    return { success: false, message: 'Workflow not configured' };
  }

  console.log('Mock Workflow Execution:', {
    workflowId: action.workflowId,
    workflowName: action.workflowName,
    recordId,
    recordData
  });

  // Simulate workflow execution
  await new Promise(resolve => setTimeout(resolve, 800));

  return {
    success: true,
    message: `Workflow "${action.workflowName || action.workflowId}" triggered successfully`,
    data: { workflowId: action.workflowId }
  };
}

/**
 * Execute field updates action
 */
async function executeFieldUpdates(
  action: ActionConfig,
  recordData: Record<string, any>,
  recordId: string,
  onUpdateFields?: (updates: Record<string, any>) => Promise<void>
): Promise<ExecutionResult> {
  if (!action.fieldUpdates || action.fieldUpdates.length === 0) {
    return { success: false, message: 'No field updates configured' };
  }

  const updates: Record<string, any> = {};
  action.fieldUpdates.forEach(update => {
    updates[update.fieldId] = update.value;
  });

  console.log('Field Updates:', { recordId, updates });

  if (onUpdateFields) {
    try {
      await onUpdateFields(updates);
      return {
        success: true,
        message: 'Fields updated successfully',
        data: updates
      };
    } catch (error) {
      return {
        success: false,
        message: `Failed to update fields: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  // If no callback, simulate update
  await new Promise(resolve => setTimeout(resolve, 300));
  return {
    success: true,
    message: 'Fields updated successfully (simulated)',
    data: updates
  };
}

/**
 * Execute navigation action
 */
function executeNavigation(
  action: ActionConfig,
  recordData: Record<string, any>,
  recordId: string,
  onNavigate?: (url: string, newTab: boolean) => void
): ExecutionResult {
  if (!action.navigateUrl) {
    return { success: false, message: 'Navigation URL not configured' };
  }

  // Replace placeholders
  let url = action.navigateUrl.replace('{recordId}', recordId);
  Object.keys(recordData).forEach(key => {
    url = url.replace(`{${key}}`, String(recordData[key] || ''));
  });

  const openInNewTab = action.openInNewTab ?? false;

  if (onNavigate) {
    onNavigate(url, openInNewTab);
  } else if (openInNewTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }

  return {
    success: true,
    message: 'Navigating...',
    data: { url, newTab: openInNewTab }
  };
}

/**
 * Execute JavaScript action
 */
function executeJavaScript(
  action: ActionConfig,
  recordData: Record<string, any>,
  recordId: string
): ExecutionResult {
  if (!action.javascriptCode) {
    return { success: false, message: 'JavaScript code not configured' };
  }

  try {
    // Create a safe execution context
    const context = {
      recordId,
      recordData,
      console: {
        log: (...args: any[]) => console.log('[Custom Button]', ...args),
        warn: (...args: any[]) => console.warn('[Custom Button]', ...args),
        error: (...args: any[]) => console.error('[Custom Button]', ...args)
      }
    };

    // Execute the code with context
    // Note: In production, you'd want to use a safer sandbox
    const func = new Function('context', `
      const { recordId, recordData, console } = context;
      ${action.javascriptCode}
    `);

    const result = func(context);

    return {
      success: true,
      message: 'JavaScript executed successfully',
      data: result
    };
  } catch (error) {
    return {
      success: false,
      message: `JavaScript error: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Execute open modal action
 */
function executeOpenModal(
  action: ActionConfig,
  recordData: Record<string, any>,
  onOpenModal?: (modalType: ModalType, recordData: Record<string, any>) => void
): ExecutionResult {
  if (!action.modalType) {
    return { success: false, message: 'Modal type not configured' };
  }

  if (onOpenModal) {
    onOpenModal(action.modalType, recordData);
    return {
      success: true,
      message: 'Modal opened',
      data: { modalType: action.modalType }
    };
  }

  return {
    success: false,
    message: 'Modal handler not configured'
  };
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get button color classes for styling
 */
export function getButtonColorClasses(color?: string): string {
  switch (color) {
    case 'purple':
      return 'bg-purple-600 hover:bg-purple-700 text-white';
    case 'blue':
      return 'bg-blue-600 hover:bg-blue-700 text-white';
    case 'green':
      return 'bg-green-600 hover:bg-green-700 text-white';
    case 'red':
      return 'bg-red-600 hover:bg-red-700 text-white';
    case 'yellow':
      return 'bg-yellow-500 hover:bg-yellow-600 text-white';
    case 'gray':
    default:
      return 'bg-gray-600 hover:bg-gray-700 text-white';
  }
}

/**
 * Get outline button color classes
 */
export function getButtonOutlineClasses(color?: string): string {
  switch (color) {
    case 'purple':
      return 'border-purple-600 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20';
    case 'blue':
      return 'border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20';
    case 'green':
      return 'border-green-600 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20';
    case 'red':
      return 'border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20';
    case 'yellow':
      return 'border-yellow-500 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20';
    case 'gray':
    default:
      return 'border-gray-400 text-gray-600 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-400 dark:hover:bg-gray-800';
  }
}
